/** @jsxImportSource @emotion/react */
import { styleguideColors as COLORS, styleguideFontSize as FONT_SIZE, styleguideFontWeight as FONT_WEIGHT } from '@/Client/Styles/kaiShikiTokens';
import { css } from '@emotion/react';
import { styleguideFontFamily as FONT, styleguideLetterSpacing as TRACKING } from '@kaiizen/kai-shiki/core';
import type { CSSProperties } from 'react';

import { InfoIcon as KaiShikiInfoIcon } from '@/Client/Icons/kaiShikiIconAliases';
import { pxToRem } from '@/Client/Styles/pxToRem';
import { CommonActionButton } from '@kaiizen/kai-shiki/react';
import {
  SETTINGS_WIDGET_COLORS,
  SETTINGS_WIDGET_COUNT_STYLE,
  SETTINGS_WIDGET_SECTION_INSET_STYLE,
  SETTINGS_WIDGET_TITLE_REGULAR_STYLE,
} from './SettingsStyleLib';

/**
 * The header's count line, structured rather than pre-joined: the design sets the leading figure
 * in bold ink, its qualifier and the rest in midtone, and the separators on the stroke colour.
 * A single string could not express any of that (KAIDEV-4345).
 */
export type SettingsHeaderCounts = {
  /** "3 modifiers" — the figure the line is about. */
  lead: string;
  /** "(10 max)", sitting with the lead rather than after a separator. */
  qualifier?: string;
  /** "2 enabled", "1 disabled" — each behind its own separator. */
  details?: readonly string[];
};

type SettingsHeaderRowProps = {
  title?: string;
  counts?: SettingsHeaderCounts;
  countsTestId?: string;
  saveDisabled?: boolean;
  onSaveClick?: () => void;
  actionLabel?: string;
  actionDisabled?: boolean;
  onActionClick?: () => void;
  hideDefaultActions?: boolean;
};

const ROW_AND_DIVIDER_STYLE = css`
  label: ROW_AND_DIVIDER_STYLE;
  ${SETTINGS_WIDGET_SECTION_INSET_STYLE}
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const ROW_STYLE = css`
  label: ROW_STYLE;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  min-height: 40px;
`;

const TITLE_META_STYLE = css`
  label: TITLE_META_STYLE;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const TITLE_STYLE = css`
  label: TITLE_STYLE;
  ${SETTINGS_WIDGET_TITLE_REGULAR_STYLE}
`;

const INFO_ICON_STYLE = css`
  label: INFO_ICON_STYLE;
  width: 24px;
  height: 24px;
  color: ${SETTINGS_WIDGET_COLORS.text};
  flex: 0 0 auto;
`;

const VERTICAL_DIVIDER_STYLE = css`
  label: VERTICAL_DIVIDER_STYLE;
  width: 1px;
  height: 20px;
  background: ${SETTINGS_WIDGET_COLORS.border};
  flex: 0 0 auto;
`;

/* A rule between the figures, not dimmed text: the design puts the separators on the stroke
   colour while the figures stay readable. */
const COUNT_SEPARATOR_STYLE = css`
  label: COUNT_SEPARATOR_STYLE;
  color: ${SETTINGS_WIDGET_COLORS.border};
  margin: 0 10px;
`;

const COUNT_STYLE = css`
  label: COUNT_STYLE;
  ${SETTINGS_WIDGET_COUNT_STYLE}
`;

const ACTIONS_STYLE = css`
  label: ACTIONS_STYLE;
  display: flex;
  align-items: center;
  gap: 10px;
`;

/* The design's `.btn`: mono, uppercase, widely tracked, square. The sweep carried the interim
   body-font label across faithfully, which kept these reading as the old design even once the
   colours moved (KAIDEV-4345). */
const BUTTON_BASE_STYLE: CSSProperties = {
  minHeight: 40,
  borderRadius: 0,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  textTransform: 'uppercase',
  whiteSpace: 'nowrap',
  fontFamily: FONT.mono,
  fontSize: pxToRem(FONT_SIZE.monoStageNum),
  fontStyle: 'normal',
  fontWeight: FONT_WEIGHT.bold,
  lineHeight: 1.167,
  letterSpacing: TRACKING.monoTight,
  padding: '11px 20px',
  border: 0,
};

/** `.btn-go`: the one primary, ink on paper. It was green, which this design does not use. */
const PRIMARY_BUTTON_STYLE: CSSProperties = {
  ...BUTTON_BASE_STYLE,
  background: SETTINGS_WIDGET_COLORS.text,
  color: COLORS.paper,
};

const PRIMARY_BUTTON_DISABLED_STYLE: CSSProperties = {
  background: SETTINGS_WIDGET_COLORS.border,
  color: SETTINGS_WIDGET_COLORS.textFieldLabel,
  cursor: 'default',
};

const HORIZONTAL_DIVIDER_STYLE = css`
  label: HORIZONTAL_DIVIDER_STYLE;
  width: 100%;
  height: 1px;
  background: ${SETTINGS_WIDGET_COLORS.border};
`;

const InfoIcon = () => (
  <span css={INFO_ICON_STYLE}>
    <KaiShikiInfoIcon size={24} />
  </span>
);

export const SettingsHeaderRow = ({
  title = 'Project Settings',
  counts = { lead: '2 project types' },
  countsTestId,
  saveDisabled = false,
  onSaveClick,
  actionLabel,
  actionDisabled = false,
  onActionClick,
  hideDefaultActions = false,
}: SettingsHeaderRowProps) => (
  <div css={ROW_AND_DIVIDER_STYLE}>
    <div css={ROW_STYLE}>
      <div css={TITLE_META_STYLE}>
        <div css={TITLE_STYLE}>{title}</div>
        <InfoIcon />
        <div css={VERTICAL_DIVIDER_STYLE} />
        <div css={COUNT_STYLE} data-testid={countsTestId}>
          <b>{counts.lead}</b>
          {counts.qualifier ? ` ${counts.qualifier}` : null}
          {counts.details?.map(detail => (
            <span key={detail}>
              <span css={COUNT_SEPARATOR_STYLE}>|</span>
              {detail}
            </span>
          ))}
        </div>
      </div>

      <div css={ACTIONS_STYLE}>
        {
          /* The section's own action — "Add Modifier" — is the primary here, because a section
             that autosaves has no Save for it to defer to (KAIDEV-4345). */
        }
        {actionLabel
          ? (
            <CommonActionButton
              style={{
                ...PRIMARY_BUTTON_STYLE,
                ...(actionDisabled ? PRIMARY_BUTTON_DISABLED_STYLE : undefined),
              }}
              disabled={actionDisabled}
              onClick={onActionClick}
            >
              {actionLabel}
            </CommonActionButton>
          )
          : null}
        {
          /* One button, as the design has it. Cancel is gone because there is nothing to
             discard: every Org Settings tab autosaves, and none of them registers a navigation
             guard any more (KAIDEV-4345). All five call sites pass `hideDefaultActions`, so this
             branch has no live caller - see the PR's open findings before relying on it. */
        }
        {!hideDefaultActions
          ? (
            <CommonActionButton
              style={{
                ...PRIMARY_BUTTON_STYLE,
                ...(saveDisabled ? PRIMARY_BUTTON_DISABLED_STYLE : undefined),
              }}
              disabled={saveDisabled}
              onClick={onSaveClick}
            >
              Save Changes
            </CommonActionButton>
          )
          : null}
      </div>
    </div>

    <div css={HORIZONTAL_DIVIDER_STYLE} />
  </div>
);
